<template>
  <section
    id="calendar-events-panel"
    role="region"
    aria-labelledby="calendar-events-heading"
    :aria-busy="!ready"
    class="panel panel-default"
  >
    <div class="panel-body">
      <header class="panel-header">
        <h2 id="calendar-events-heading">Upcoming Events</h2>
      </header>
      <p class="sr-only" role="status" aria-live="polite">{{ statusMessage }}</p>
      <p v-if="hasScheduleLoadError" class="sr-only" role="alert">{{ statusMessage }}</p>

      <div v-if="ready" class="calendar-events-content">
        <ul v-if="upcomingVisibleEvents.length" class="upcoming-events-list" aria-label="Upcoming programme events">
          <li
            v-for="event in upcomingVisibleEvents"
            :key="event.id"
            class="upcoming-event-card"
            :class="`event-${event.eventType}`"
          >
            <div class="event-icon" :class="`event-icon-${event.eventType}`">
              <i :class="event.icon" aria-hidden="true"></i>
            </div>
            <div class="event-content">
              <h3 class="event-content-header">
                <span class="event-type-label">{{ event.label }}</span>:
                <router-link :to="getEventRoute(event)"> {{ event.title }}</router-link>
              </h3>
              <p>{{ event.displayDate }}</p>
            </div>
          </li>
        </ul>
        <empty-text-placeholder v-else
          empty-text="No upcoming meetings or events at the moment."
        ></empty-text-placeholder>
        <hr class="horizontal-hr">

        <v-calendar
          :is-dark="isDark"
          :attributes="calendarAttributes"
          :initial-page="calendarInitialPage"
          view="weekly"
          title-position="left"
          :first-day-of-week="firstDayOfWeek"
          class="homepage-weekly-calendar"
        >
          <template #footer>
            <div class="calendar-footer">
              <router-link
                :to="{ name: 'programme_schedule', params: { programmeId: programmeId } }"
                class="btn btn-outlined full-calendar-btn"
                target="_blank"
                rel="noopener noreferrer"
              >
                <i class="fas fa-calendar-alt" aria-hidden="true"></i> Full Programme Schedule
                <span class="sr-only">(opens in new tab)</span>
              </router-link>
            </div>
          </template>
        </v-calendar>
      </div>
      <slot></slot>
    </div>
  </section>
</template>

<style scoped>
.calendar-events-content {
  display: flex;
  flex-direction: column;
}

.upcoming-events-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.upcoming-event-card {
  display: flex;
  align-items: flex-start;
  gap: 10px;
}

.event-content h3 {
  margin: 0;
  color: var(--kate-type-light);
  line-height: 1.15;
  font-size: 1rem;
  font-family: hk-grotesk-light, sans-serif;
}

.event-content p {
  margin: 2px 0 0;
  color: var(--kate-type-alt);
}

.event-icon {
  width: 28px;
  min-width: 28px;
  text-align: center;
  padding-top: 3px;
  font-size: 1.25rem;
}

.event-type-label {
  font-family: hk-grotesk-semibold, sans-serif;
}

.event-module_start .event-type-label,
.event-icon-module_start {
  color: var(--kate-logo-green);
}

.event-module_completion .event-type-label,
.event-icon-module_completion {
  color: var(--kate-logo-orange);
}

.event-calendar_event .event-type-label,
.event-icon-calendar_event {
  color: var(--kate-danger-alt);
}

.event-meeting .event-type-label,
.event-icon-meeting {
  color: var(--kate-logo-blue);
}

.homepage-weekly-calendar {
  margin-top: 4px;
}

.calendar-footer {
  width: 100%;
  padding: 8px 0 0;
}

.full-calendar-btn {
  width: 100%;
  display: inline-flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
}

:deep(.vc-container) {
  width: 100%;
  background: transparent;
  border: 0;
  color: var(--kate-type-light);
  font-family: hk-grotesk-light, sans-serif;
}

:deep(.vc-header) {
  padding-left: 0;
  padding-right: 0;
}

:deep(.vc-title) {
  font-family: hk-grotesk-semibold, sans-serif;
}

:deep(.vc-weekdays) {
  padding: 0;
}

:deep(.vc-day-content) {
  color: var(--kate-type-light);
}

:deep(.vc-day) {
  min-height: 54px;
}
</style>

<script>
import useThemeStore from '@stores/theme';
import TimeMixin from '@mixins/time-mixins';
import {
  HOMEPAGE_CALENDAR_FIRST_DAY_OF_WEEK,
  getWeeklyPageAddress,
} from '@utils/homepage-weekly-calendar';
import EmptyTextPlaceholder from '../components/empty-text-placeholder.vue';

export default {
  components: {
    EmptyTextPlaceholder,
  },
  mixins: [TimeMixin],

  props: {
    programmeId: {
      type: Number,
      required: true,
    },
    learnerInfo: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      themeStore: useThemeStore(),
      calendarEventsReady: false,
      hasScheduleLoadError: false,
      today: new Date(),
      calendarEvents: [],
      eventMeta: {
        meeting: {
          label: 'Meeting',
          icon: 'far fa-user-group',
          color: 'var(--kate-logo-blue)',
        },
        calendar_event: {
          label: 'Live Event',
          icon: 'far fa-video',
          color: 'var(--kate-danger-alt)',
        },
        module_start: {
          label: 'Module Start',
          icon: 'far fa-book-open',
          color: 'var(--kate-logo-green)',
        },
        module_completion: {
          label: 'Module End',
          icon: 'far fa-book-blank',
          color: 'var(--kate-logo-orange)',
        },
      },
    };
  },

  beforeMount() {
    this.getProgrammeCalendarEvents();
  },

  watch: {
    programmeId() {
      this.getProgrammeCalendarEvents();
    },
  },

  computed: {
    firstDayOfWeek() {
      return HOMEPAGE_CALENDAR_FIRST_DAY_OF_WEEK;
    },
    ready() {
      return this.calendarEventsReady;
    },
    statusMessage() {
      if (!this.ready) {
        return 'Loading upcoming events.';
      }
      if (this.hasScheduleLoadError) {
        return 'Unable to load upcoming events right now.';
      }
      if (this.upcomingVisibleEvents.length) {
        return `${this.upcomingVisibleEvents.length} upcoming events shown.`;
      }
      return 'No upcoming events found.';
    },
    eventRouteMap() {
      return {
        meeting: { name: 'module_meeting' },
        calendar_event: { name: 'module_calendar_event' },
        module_start: { name: 'module_landing' },
        module_completion: { name: 'module_landing' },
      };
    },
    normalisedEvents() {
      return this.calendarEvents
        .filter(event => this.eventMeta[event.event_type])
        .filter(event => event.start_time)
        .map(event => {
          const startDate = this.getDate(event.start_time);
          const meta = this.eventMeta[event.event_type];
          const title = event.asset_name || event.module_name || 'Untitled';
          return {
            id: `${event.event_type}_${event.module_asset_id || event.module_id || event.id || title}`,
            eventType: event.event_type,
            label: meta.label,
            icon: meta.icon,
            color: meta.color,
            title,
            startDate,
            displayDate: this.formatEventDate(event),
            moduleId: event.module_id,
            moduleAssetId: event.module_asset_id,
          };
        })
        .sort((a, b) => a.startDate - b.startDate);
    },
    upcomingVisibleEvents() {
      const now = Date.now();
      return this.normalisedEvents
        .filter(event => event.startDate.getTime() >= now)
        .slice(0, 4);
    },
    isDark() {
      return this.themeStore.theme !== 'light';
    },
    calendarAttributes() {
      const attributes = this.normalisedEvents.map(event => ({
        key: `dot-${event.id}`,
        dates: event.startDate,
        dot: {
          style: {
            backgroundColor: event.color,
          },
        },
        customData: {
          eventType: event.eventType,
          title: event.title,
        },
        popover: {
          label: `${event.title} (${event.displayDate})`,
          visibility: 'click',
          hideIndicator: false,
          indicator: {
            color: event.color,
          },
        },
      }));

      attributes.push({
        key: 'focus-day',
        dates: this.today,
        highlight: {
          fillMode: 'outline',
          color: 'var(--kate-primary)',
        },
      });

      return attributes;
    },

    calendarInitialPage() {
      return getWeeklyPageAddress(this.today, this.firstDayOfWeek);
    },
  },

  methods: {
    formatEventDate(event) {
      const isAllDay = event.event_type === 'module_start' || event.event_type === 'module_completion';
      if (isAllDay) {
        return this.parseTimestamp(event.start_time, undefined, 'MMM d');
      }
      return this.parseTimestamp(event.start_time, undefined, 'MMM d, h:mm aaa');
    },
    getEventRoute(normalisedEvent) {
      const baseRoute = this.eventRouteMap[normalisedEvent.eventType];
      const params = { moduleId: normalisedEvent.moduleId };
      if (normalisedEvent.eventType === 'meeting') {
        params.moduleMeetingId = normalisedEvent.moduleAssetId;
      }
      if (normalisedEvent.eventType === 'calendar_event') {
        params.moduleCalendarEventId = normalisedEvent.moduleAssetId;
      }
      return {
        ...baseRoute,
        params,
      };
    },
    getProgrammeCalendarEvents() {
      if (!this.programmeId) {
        this.calendarEvents = [];
        this.calendarEventsReady = true;
        return;
      }
      this.$Loading.start();
      this.calendarEventsReady = false;
      this.hasScheduleLoadError = false;
      this.$logger.info('Getting programme schedule data', { programmeId: this.programmeId });
      this.$http.get(`/api/curriculum/programmes/${this.programmeId}/schedule`)
        .then(response => {
          this.calendarEvents = response.data.schedule || response.data.calendar_events || [];
          this.$logger.info('Got programme schedule data', { programmeId: this.programmeId });
        })
        .catch(err => {
          this.calendarEvents = [];
          this.hasScheduleLoadError = true;
          this.$logger.autowarn('Error getting programme schedule data', { programmeId: this.programmeId }, err);
        }).then(() => {
          this.calendarEventsReady = true;
          this.$Loading.finish();
        });
    },
  },
};
</script>
